import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Box, Button, Icon, Tooltip, Typography } from '@popmenu/common-ui';
import { Info } from '@popmenu/web-icons';
import { nl2br } from '../../../utils/react';
import { makeStyles } from '../../../utils/withStyles';
import type { RootPlaceMenuItemQuery_rootPlace_RootPlace_menuItem_MenuItem as DishType } from '../../../libs/gql/queries/root_places/rootPlaceMenuItemQuery.generated';

const useStyles = makeStyles(theme => ({
  categoryName: {
    borderColor: theme.palette.blue.main,
    borderRadius: theme.spacing(1),
    color: theme.palette.blue.main,
    fontSize: '1rem',
    lineHeight: '2rem',
    marginTop: theme.spacing(3),
    textTransform: 'none',
  },
  description: {
    fontStyle: 'italic',
  },
  generatedDescription: {
    '&:last-child': {
      marginBottom: 0,
    },
    marginBottom: theme.spacing(3),
  },
  generatedDescriptionIcon: {
    color: '#0076CB',
    display: 'inline-block',
    marginLeft: theme.spacing(0.5),
    transform: 'scale(1.2)',
  },
  title: {
    borderBottom: `2px solid ${theme.palette.text.primary}`,
    fontSize: '0.95rem',
    fontWeight: 'bold',
    margin: theme.spacing(3, 0, 3, 0),
    paddingBottom: theme.spacing(1),
    textAlign: 'center',
    textTransform: 'uppercase',
  },
}));

export const DishModalDishDetails = ({ dish, placeSlug }: { dish: DishType, placeSlug: string }) => {
  const classes = useStyles();

  return (
    <Box component="section">
      <Typography className={classes.title} component="h2">
        <FormattedMessage
          id="dish_modal_dish_details.title"
          defaultMessage="Dish Details"
        />
      </Typography>
      <Box>
        {!dish.generatedDescription && !dish.description && (
          <Typography>
            <FormattedMessage
              id="dish_modal_dish_details.default_dish_description"
              defaultMessage="{dishName} from {restaurantName} in {city}, {stateOrCountry}."
              values={{
                city: dish.location?.city,
                dishName: dish.displayName,
                restaurantName: dish.restaurant.name,
                stateOrCountry: dish.location?.state || dish.location?.countryName,
              }}
            />
          </Typography>
        )}
        {dish.generatedDescription && (
          <Typography className={classes.generatedDescription}>
            {dish.generatedDescription}
            <Tooltip
              title={(
                <FormattedMessage
                  id="dish_modal_dish_details.generated_description_info"
                  defaultMessage="This dish description was generated with the help of AI, using information and photos from the restaurant. Generative AI is experimental and info quality may vary. Please view the menu on the restaurant website for the most accurate details and information."
                />
              )}
            >
              <Box className={classes.generatedDescriptionIcon} tabIndex={0}>
                <Icon icon={Info} />
              </Box>
            </Tooltip>
          </Typography>
        )}
        {dish.description && (
          <Typography className={classes.description}>
            <FormattedMessage
              id="dish_modal_dish_details.from_restaurant_name"
              defaultMessage="From {restaurantName}:"
              values={{
                restaurantName: dish.restaurant.name,
              }}
            />
            <br />
            <br />
            {nl2br(dish.description)}
          </Typography>
        )}
      </Box>
      {dish.generatedCategoryName && dish.rootDishCategorySlug && (
        <Button
          className={classes.categoryName}
          component={Link}
          role="link"
          to={`/places/${placeSlug}/categories/${dish.rootDishCategorySlug}`}
          variant="outlined"
        >
          {dish.generatedCategoryName}
        </Button>
      )}
    </Box>
  );
};
